import {useRef, useState, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {Checkbox} from '@/components/ui/checkbox'
import {Hint} from '@/components/ui/hint'
import {InfoTip} from '@/components/ui/info-tip'
import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger} from '@/components/ui/dialog'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {DescargarEvidencia} from '@/components/descargar-evidencia'
import {EmployeeConceptosSection, motivoAnulado, veredictoTone} from '@/components/employee-conceptos-section'
import {ValoracionesSection} from '@/components/valoraciones-section'
import {ExamenNormaAnteriorSalidas, ExamenNormaAnteriorSellos} from '@/components/examen-norma-anterior'
import {FieldError} from '@/components/field-error'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {
  EXAMENES_PAGE_SIZE,
  useEmployeeExamenes,
  useRegistrarExamen,
  useCorregirExamen,
  type ConceptoAptitud,
  type ExamenOcupacional,
  type ExamenTipo,
} from '@/hooks/sgsst/evidencias/use-examenes'
import {
  EVIDENCE_ACCEPT,
  EVIDENCE_ALLOWED_TYPES,
  EVIDENCE_MAX_BYTES,
  evidenceTooBig,
  mintUploadTarget,
  rutaDeDescarga,
  uploadToStorage,
} from '@/hooks/sgsst/evidencias/use-evidence'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {translateApiError} from '@/lib/errors'
import {localToday, formatDate, formatDateOnly} from '@/lib/risk'
import {validateRequired} from '@/lib/validation'
import {cn} from '@/lib/utils'

// Los seis tipos de examen exigibles por la Res. 1843/2025.
const TIPOS: ExamenTipo[] = ['ingreso', 'periodico', 'cambio_ocupacion', 'egreso', 'post_incapacidad', 'reintegro']
// La Res. 1843/2025 retiró «no apto» del concepto: solo entra declarado, por la casilla del
// veredicto textual (E1-H06).
const CONCEPTOS: ConceptoAptitud[] = ['apto', 'apto_con_restricciones']

function TipoField({
  value,
  onChange,
  testId,
}: {
  value: ExamenTipo
  onChange: (value: ExamenTipo) => void
  testId: string
}) {
  const {t} = useTranslation()
  return (
    <label className="block space-y-1">
      <span className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
        {t('empleadoExamenes.registrar.tipoLabel')}
      </span>
      <select
        value={value}
        data-testid={testId}
        onChange={(e) => onChange(e.target.value as ExamenTipo)}
        className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm shadow-sm focus:border-ring focus:outline-none">
        {TIPOS.map((tipo) => (
          <option key={tipo} value={tipo}>
            {t(`empleadoExamenes.tipo.${tipo}`)}
          </option>
        ))}
      </select>
    </label>
  )
}

function ConceptoField({
  value,
  onChange,
  testId,
  textualTestId,
  hint,
  // «no apto» ya registrado: el backend rechaza reescribirlo (422), así que la casilla se apaga.
  registrado,
}: {
  value: ConceptoAptitud
  onChange: (value: ConceptoAptitud) => void
  testId: (concepto: ConceptoAptitud) => string
  textualTestId: string
  hint?: string
  registrado?: boolean
}) {
  const {t} = useTranslation()
  const textual = value === 'no_apto'
  const bloqueado = !!registrado
  // Destildar vuelve al concepto elegido antes, nunca a «apto» forzado: degradaría un «apto con restricciones».
  const [previo, setPrevio] = useState<ConceptoAptitud>(textual ? 'apto' : value)
  return (
    <div className="space-y-2">
      <span className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
        {t('empleadoExamenes.registrar.conceptoLabel')}
      </span>
      <div className="flex flex-col gap-2">
        {CONCEPTOS.map((concepto) => (
          <button
            key={concepto}
            type="button"
            disabled={bloqueado}
            data-testid={testId(concepto)}
            onClick={() => {
              setPrevio(concepto)
              onChange(concepto)
            }}
            className={cn(
              'min-h-11 w-full rounded-lg border px-3 text-left text-sm font-medium',
              value === concepto ? 'border-primary bg-accent text-accent-foreground' : 'border-border',
              bloqueado && 'opacity-50'
            )}>
            {t(`empleadoExamenes.concepto.${concepto}`)}
          </button>
        ))}
      </div>
      {hint && <Hint>{hint}</Hint>}
      {/* Única entrada de «no apto» (E1-H06): se transcribe del certificado, nunca se propone. */}
      <div className="flex items-start gap-2 rounded-lg border border-border p-3">
        <Checkbox
          checked={textual}
          disabled={bloqueado}
          data-testid={textualTestId}
          onCheckedChange={(next) => onChange(next ? 'no_apto' : previo)}
        />
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">{t('examenes.normaAnterior.registrarTextual')}</p>
          <p className="text-xs text-muted-foreground">{t('examenes.normaAnterior.registrarTextualHint')}</p>
          {bloqueado && (
            <p className="text-xs text-muted-foreground" data-testid={`${textualTestId}-bloqueada`}>
              {t('examenes.normaAnterior.noSeReescribe')}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

function FechaExamenField({
  value,
  onChange,
  error,
  onBlurValidate,
  testId,
}: {
  value: string
  onChange: (value: string) => void
  error: string | undefined
  onBlurValidate: (value: string) => void
  testId: string
}) {
  const {t} = useTranslation()
  return (
    <>
      <label className="block space-y-1">
        <span className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
          {t('empleadoExamenes.registrar.fechaLabel')}
        </span>
        <input
          type="date"
          value={value}
          data-testid={testId}
          max={localToday()} // nosemgrep: browser-clock-business-date -- date input max, legitimate data-entry (not a compliance decision)
          onChange={(e) => onChange(e.target.value)}
          onBlur={(e) => onBlurValidate(e.target.value)}
          aria-invalid={!!error}
          className="w-full rounded-lg border border-input bg-background px-3 py-2 font-mono text-sm shadow-sm focus:border-ring focus:outline-none"
        />
      </label>
      <FieldError error={error} />
    </>
  )
}

/** Solo el hecho y el concepto de aptitud: nunca diagnóstico ni historia clínica (custodia de salud). */
function RegistrarExamenDialog({businessId, employeeId}: {businessId: string; employeeId: string}) {
  const {t} = useTranslation()
  const [open, setOpen] = useState(false)
  const [tipo, setTipo] = useState<ExamenTipo>('ingreso')
  const [concepto, setConcepto] = useState<ConceptoAptitud>('apto')
  const [fecha, setFecha] = useState(localToday()) // nosemgrep: browser-clock-business-date -- date input default value, legitimate data-entry (not a compliance decision)
  const [fechaError, setFechaError] = useState<string | undefined>()
  const [file, setFile] = useState<File | null>(null)
  const [atestacion, setAtestacion] = useState(false)
  const [progress, setProgress] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [confirmandoDescarte, setConfirmandoDescarte] = useState(false)
  const [dialogo, setDialogo] = useState<HTMLElement | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const registrar = useRegistrarExamen(businessId, employeeId)

  function hayTrabajo() {
    // nosemgrep: browser-clock-business-date -- compara contra el default del propio formulario, no es una decisión de cumplimiento
    return !!file || tipo !== 'ingreso' || concepto !== 'apto' || fecha !== localToday()
  }

  function pedirCierre() {
    if (hayTrabajo()) return setConfirmandoDescarte(true)
    setOpen(false)
  }

  function reset() {
    setTipo('ingreso')
    setConcepto('apto')
    setFecha(localToday()) // nosemgrep: browser-clock-business-date -- dialog reset back to today, legitimate data-entry default
    setFechaError(undefined)
    setFile(null)
    setAtestacion(false)
    setProgress(null)
    setError(null)
    setConfirmandoDescarte(false)
    if (inputRef.current) inputRef.current.value = ''
  }

  function pickFile(files: FileList | null) {
    const picked = files?.[0]
    if (!picked) return
    setError(null)
    if (!EVIDENCE_ALLOWED_TYPES.has(picked.type)) return setError(t('errors.evidenceBadFormat'))
    if (picked.size > EVIDENCE_MAX_BYTES) return setError(evidenceTooBig(t, picked))
    setFile(picked)
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    const err = validateRequired(fecha)
    setFechaError(err ?? undefined)
    if (err) return
    setError(null)
    try {
      let soporte: Partial<{
        object_key: string
        filename: string
        content_type: string
        size_bytes: number
      }> = {}
      if (file) {
        setProgress(0)
        const target = await mintUploadTarget(businessId, file)
        await uploadToStorage(target, file, setProgress)
        soporte = {object_key: target.object_key, filename: file.name, content_type: file.type, size_bytes: file.size}
      }
      await registrar.mutateAsync({
        tipo,
        concepto_aptitud: concepto,
        fecha,
        veredicto_textual_certificado: concepto === 'no_apto',
        atestacion,
        ...soporte,
      })
      setProgress(null)
      setOpen(false)
    } catch (err) {
      setProgress(null)
      setError(translateApiError(err))
    }
  }

  return (
    <>
      {/* Se repliega bajo la confirmación de descarte: dos modales a la vez se pelean el foco. */}
      <Dialog
        open={open && !confirmandoDescarte}
        onOpenChange={(next) => {
          if (!next) return pedirCierre()
          setOpen(true)
          reset()
        }}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" data-testid="examen-registrar">
            {t('empleadoExamenes.registrar.action')}
          </Button>
        </DialogTrigger>
        <DialogContent ref={setDialogo}>
          <DialogHeader>
            <DialogTitle>{t('empleadoExamenes.registrar.title')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" noValidate>
            <TipoField value={tipo} onChange={setTipo} testId="examen-registrar-tipo" />

            <ConceptoField
              value={concepto}
              onChange={setConcepto}
              testId={(value) => `examen-registrar-concepto-${value}`}
              textualTestId="examen-registrar-textual"
              hint={t('empleadoExamenes.registrar.conceptoHint')}
            />

            <FechaExamenField
              value={fecha}
              onChange={(value) => {
                setFecha(value)
                setFechaError(undefined)
              }}
              error={fechaError}
              onBlurValidate={(value) => setFechaError(validateRequired(value) ?? undefined)}
              testId="examen-registrar-fecha"
            />

            <div className="space-y-1">
              <span className="flex items-center gap-1.5 font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
                {t('empleadoExamenes.registrar.soporteLabel')}
                <InfoTip
                  label={t('empleadoExamenes.registrar.soporteTipLabel')}
                  tip={t('empleadoExamenes.registrar.soporteTip')}
                  contenedor={dialogo}
                  testId="examen-registrar-soporte-tip"
                />
              </span>
              <input
                ref={inputRef}
                type="file"
                accept={EVIDENCE_ACCEPT}
                hidden
                data-testid="examen-registrar-soporte-input"
                onChange={(e) => pickFile(e.target.files)}
              />
              {file ? (
                <div className="flex items-center gap-2">
                  <p className="truncate text-sm text-foreground" data-testid="examen-registrar-soporte-elegido">
                    {file.name}
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    data-testid="examen-registrar-soporte-cambiar"
                    onClick={() => {
                      setFile(null)
                      if (inputRef.current) inputRef.current.value = ''
                      inputRef.current?.click()
                    }}>
                    {t('empleadoExamenes.registrar.soporteCambiar')}
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  data-testid="examen-registrar-soporte-elegir"
                  onClick={() => inputRef.current?.click()}>
                  {t('empleadoExamenes.registrar.soporteElegir')}
                </Button>
              )}
              {progress !== null && (
                <div className="h-1.5 w-32 overflow-hidden rounded-full bg-secondary">
                  <div className="h-full rounded-full bg-primary transition-all" style={{width: `${progress}%`}} />
                </div>
              )}
              {/* La atestación es por documento: sin ella el soporte va a Cuarentena y Editar
                no permite volver a adjuntarlo, por eso el aviso queda visible. */}
              {file && (
                <div className="space-y-1 pt-1">
                  <label className="flex items-start gap-2 text-sm">
                    <Checkbox
                      checked={atestacion}
                      data-testid="examen-registrar-atestacion"
                      onCheckedChange={(next) => setAtestacion(next === true)}
                    />
                    <span>{t('empleadoExamenes.registrar.atestacionLabel')}</span>
                  </label>
                  <Hint>{t('empleadoExamenes.registrar.atestacionHint')}</Hint>
                </div>
              )}
            </div>

            <FormDialogFooter
              error={error}
              pending={registrar.isPending || progress !== null}
              submitLabel={t('empleadoExamenes.registrar.submit')}
              onCancel={pedirCierre}
            />
          </form>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={confirmandoDescarte}
        onOpenChange={(next) => !next && setConfirmandoDescarte(false)}
        title={t('empleadoExamenes.registrar.descartarTitulo')}
        description={t('empleadoExamenes.registrar.descartarDescripcion')}
        confirmLabel={t('empleadoExamenes.registrar.descartar')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => {
          setConfirmandoDescarte(false)
          setOpen(false)
        }}
      />
    </>
  )
}

/** Corrige tipo, concepto y fecha; el soporte no se edita: el backend lo mueve al tipo corregido. */
function EditarExamenDialog({
  businessId,
  employeeId,
  examen,
}: {
  businessId: string
  employeeId: string
  examen: ExamenOcupacional
}) {
  const {t} = useTranslation()
  const [open, setOpen] = useState(false)
  const [tipo, setTipo] = useState<ExamenTipo>(examen.tipo)
  const [concepto, setConcepto] = useState<ConceptoAptitud>(examen.concepto_aptitud)
  const [fecha, setFecha] = useState(examen.fecha)
  const [fechaError, setFechaError] = useState<string | undefined>()
  const [error, setError] = useState<string | null>(null)
  const corregir = useCorregirExamen(businessId, employeeId)

  function reset() {
    setTipo(examen.tipo)
    setConcepto(examen.concepto_aptitud)
    setFecha(examen.fecha)
    setFechaError(undefined)
    setError(null)
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    const err = validateRequired(fecha)
    setFechaError(err ?? undefined)
    if (err) return
    setError(null)
    try {
      await corregir.mutateAsync({
        examenId: examen.id,
        patch: {
          tipo,
          concepto_aptitud: concepto,
          fecha,
          veredicto_textual_certificado: concepto === 'no_apto',
        },
      })
      setOpen(false)
    } catch (err) {
      setError(translateApiError(err))
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (next) reset()
      }}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid={`empleado-examen-editar-${examen.id}`}>
          {t('empleadoExamenes.editar.action')}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('empleadoExamenes.editar.title')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <TipoField value={tipo} onChange={setTipo} testId={`empleado-examen-editar-tipo-${examen.id}`} />

          <ConceptoField
            value={concepto}
            onChange={setConcepto}
            testId={(value) => `empleado-examen-editar-concepto-${examen.id}-${value}`}
            textualTestId={`empleado-examen-editar-textual-${examen.id}`}
            registrado={examen.concepto_aptitud === 'no_apto'}
          />

          <FechaExamenField
            value={fecha}
            onChange={(value) => {
              setFecha(value)
              setFechaError(undefined)
            }}
            error={fechaError}
            onBlurValidate={(value) => setFechaError(validateRequired(value) ?? undefined)}
            testId={`empleado-examen-editar-fecha-${examen.id}`}
          />

          <FormDialogFooter
            error={error}
            pending={corregir.isPending}
            submitLabel={t('common.save')}
            onCancel={() => setOpen(false)}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** Sin «Eliminar»: un examen registrado se anula, nunca se borra (spec §4). */
export function EmployeeExamenesSection({
  businessId,
  employeeId,
  employeeName,
  canOperate,
  canDecidir,
  decisionBloqueadaPor,
}: {
  businessId: string
  employeeId: string | null
  employeeName: string
  canOperate: boolean
  // Custodia de salud (spec §4): solo el responsable SG-SST con membresía real; el dueño
  // firma, no custodia, y modo dios no decide.
  canDecidir: boolean
  decisionBloqueadaPor: string | null
}) {
  const {t} = useTranslation()
  const godReadOnly = useGodModeReadOnly()
  const [page, setPage] = useState(1)
  const {data, isLoading} = useEmployeeExamenes(businessId, employeeId, page)
  const examenes = data?.items

  if (isLoading && !data) return <Skeleton className="h-24 w-full" />
  const canEdit = canOperate && !godReadOnly && !!employeeId
  return (
    <div className="space-y-3">
      {/* Refleja la API, que hoy deja al dueño registrar el examen con su concepto; la contradicción
          con «Registrar concepto» está pendiente en docs/modulos/perfil-cargo-spec.md. */}
      {canEdit && employeeId && (
        <div className="flex justify-end">
          <RegistrarExamenDialog businessId={businessId} employeeId={employeeId} />
        </div>
      )}
      {!examenes || examenes.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="empleado-examenes-empty">
          {t('empleadoExamenes.empty', {name: employeeName})}
        </p>
      ) : (
        <ul className="divide-y divide-border" data-testid="empleado-examenes-list">
          {examenes.map((examen) => (
            <li key={examen.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">
                  {t(`empleadoExamenes.tipo.${examen.tipo}`)}
                  <span className="ml-1.5 font-mono text-xs text-muted-foreground">{formatDateOnly(examen.fecha)}</span>
                </p>
                {examen.evidence_id && examen.evidence_filename && (
                  <DescargarEvidencia
                    ruta={rutaDeDescarga(businessId, examen.evidence_id)}
                    reservado={!canDecidir}
                    className="mt-0.5 block w-full truncate text-xs text-primary hover:underline"
                    testId={`empleado-examen-soporte-${examen.id}`}>
                    {examen.evidence_filename}
                  </DescargarEvidencia>
                )}
                <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">
                  {t('empleadoExamenes.registradoEl', {fecha: formatDate(examen.created_at)})}
                </p>
                {examen.editado_en && (
                  <p
                    className="mt-0.5 font-mono text-[11px] text-muted-foreground"
                    data-testid={`empleado-examen-editado-${examen.id}`}>
                    {t('empleadoExamenes.editadoEl', {fecha: formatDate(examen.editado_en)})}
                  </p>
                )}
                {examen.anulado_en && (
                  <p
                    className="mt-0.5 text-xs text-muted-foreground"
                    data-testid={`empleado-examen-anulado-${examen.id}`}>
                    {t('examenes.anulado', {motivo: motivoAnulado(examen.anulado_motivo, t)})}
                  </p>
                )}
                <ExamenNormaAnteriorSellos examen={examen} />
                {employeeId && (
                  <ExamenNormaAnteriorSalidas
                    businessId={businessId}
                    employeeId={employeeId}
                    examen={examen}
                    canDecidir={canDecidir}
                    bloqueadoPor={decisionBloqueadaPor}
                  />
                )}
              </div>
              <div className="flex items-center gap-2">
                <Tag
                  tone={veredictoTone(examen.concepto_aptitud, {
                    anulado: !!examen.anulado_en,
                    normaAnterior: examen.norma_anterior,
                  })}
                  data-testid={`empleado-examen-concepto-${examen.id}`}>
                  {t(`empleadoExamenes.concepto.${examen.concepto_aptitud}`)}
                </Tag>
                {canEdit && employeeId && !examen.anulado_en && (
                  <EditarExamenDialog businessId={businessId} employeeId={employeeId} examen={examen} />
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {!!data?.total && (
        <PaginationBar page={data.page} pageSize={EXAMENES_PAGE_SIZE} total={data.total} onPageChange={setPage} />
      )}
      <EmployeeConceptosSection
        businessId={businessId}
        employeeId={employeeId}
        canOperate={canOperate}
        // Registrar el concepto es la misma custodia que decidir un «no apto» (spec §4).
        canRegistrar={canDecidir}
        registroBloqueadoPor={decisionBloqueadaPor}
      />
      {/* E1-H09: registrar una valoración es atestación de firmante, no custodia de salud;
          el componente lee su propia capacidad. */}
      <ValoracionesSection businessId={businessId} employeeId={employeeId} canOperate={canOperate} />
    </div>
  )
}
